import { useState, useEffect, useRef } from "react";
import { interviewChat, getInterviewTranscript, extractFromTranscript, extractInsights, analyzeTranscript, FeatureDefinition, QualitativeResult, ExtractedInsightAPI } from "../api";
import InterviewActivities from "./InterviewActivities";

export interface ExtractedInsight {
  id: number;
  timestamp: string;
  text: string;
  type: "variable" | "belief" | "threshold";
  confidence: number;
  variable_name?: string;
  predictive_power?: number;
  prior_mean?: number;
  prior_std?: number;
  mechanism?: string;
  thresholds?: string[];
  boundary_conditions?: string[];
  source_quote?: string;
}

interface Props {
  onFinish: (insights: ExtractedInsight[], expert: string, duration: number, transcript: string) => void;
  onBack?: () => void;
  onFeatureCreated?: (feature: FeatureDefinition) => void;
  onQualitativeReady?: (result: QualitativeResult) => void;
}

interface ExpertProfile {
  id: string;
  name: string;
  title: string;
  bio: string;
  credentials: string[];
  hourlyRate: number;
  initials: string;
  avatarBg: string;
  specialty: string;
  availability: "available" | "busy" | "offline";
  rating: number;
  interviews: number;
}

const EXPERTS: ExpertProfile[] = [
  {
    id: "rn", name: "Dr. Rafael Navarro", title: "Former Lead Data Scientist, Gro Intelligence",
    bio: "12 years building agricultural commodity models. Specialist in feature engineering for weather-dependent yield prediction. Built Gro's wheat and corn signal pipeline covering 40+ countries.",
    credentials: ["PhD Statistics, Chicago", "Ex-Gro Intelligence", "30+ published signals"],
    hourlyRate: 450, initials: "RN", avatarBg: "linear-gradient(135deg, rgba(59,130,246,0.4), rgba(99,102,241,0.3))",
    specialty: "Agricultural Commodities & Feature Engineering", availability: "available", rating: 4.9, interviews: 187,
  },
  {
    id: "mc", name: "Margaret Chen", title: "Former VP Store Strategy, Baskin-Robbins",
    bio: "30 years leading store analytics across 2,400 locations. Built the internal site-selection model used to open 800+ stores. Specialist in retail location intelligence and consumer behavior patterns.",
    credentials: ["MBA Wharton", "30yr Retail Analytics", "2,400 stores analyzed"],
    hourlyRate: 400, initials: "MC", avatarBg: "linear-gradient(135deg, rgba(196,181,253,0.5), rgba(147,197,253,0.5))",
    specialty: "Retail Location Intelligence", availability: "available", rating: 4.9, interviews: 203,
  },
  {
    id: "kp", name: "Dr. Karen Park", title: "Former Head of Actuarial Modeling, Swiss Re",
    bio: "15 years in catastrophe and emerging risk modeling. Led the team that built Swiss Re's first cyber insurance pricing model from expert judgment. Specialist in prior elicitation for novel risk categories.",
    credentials: ["PhD Actuarial Science, Waterloo", "FCAS", "Ex-Swiss Re"],
    hourlyRate: 550, initials: "KP", avatarBg: "linear-gradient(135deg, rgba(253,224,71,0.4), rgba(245,158,11,0.3))",
    specialty: "Insurance Risk Modeling & Expert Priors", availability: "available", rating: 5.0, interviews: 156,
  },
  {
    id: "jm", name: "James Moretti", title: "Senior Research Analyst, Nielsen Consumer Insights",
    bio: "10 years translating qualitative consumer research into quantitative models. Has coded 500+ expert interviews into structured variables for CPG clients including Coca-Cola, P&G, and Unilever.",
    credentials: ["MS Applied Stats, Michigan", "500+ interviews coded", "Ex-Nielsen"],
    hourlyRate: 325, initials: "JM", avatarBg: "linear-gradient(135deg, rgba(134,239,172,0.4), rgba(59,130,246,0.3))",
    specialty: "Consumer Research & Qual-to-Quant", availability: "available", rating: 4.7, interviews: 312,
  },
  {
    id: "at", name: "Dr. Aisha Thompson", title: "Principal Data Scientist, Citadel Securities (Former)",
    bio: "8 years in quantitative research at Citadel and Point72. Built alternative data signal pipelines from unstructured sources — satellite imagery, NLP on earnings calls, supply chain indicators.",
    credentials: ["PhD CS, MIT", "Ex-Citadel", "Ex-Point72"],
    hourlyRate: 600, initials: "AT", avatarBg: "linear-gradient(135deg, rgba(59,130,246,0.4), rgba(99,102,241,0.3))",
    specialty: "Alternative Data & Signal Research", availability: "busy", rating: 4.9, interviews: 89,
  },
  {
    id: "lw", name: "Dr. Luis Webber", title: "Head of Clinical Biostatistics, Rho (Former)",
    bio: "20 years designing Bayesian adaptive clinical trials. Led expert elicitation for 40+ FDA submissions. Specialist in turning KOL opinions into defensible prior distributions for regulatory review.",
    credentials: ["PhD Biostatistics, Harvard", "40+ FDA submissions", "Ex-Rho, Ex-ICON"],
    hourlyRate: 500, initials: "LW", avatarBg: "linear-gradient(135deg, rgba(168,85,247,0.3), rgba(196,181,253,0.4))",
    specialty: "Bayesian Trial Design & Expert Elicitation", availability: "available", rating: 4.8, interviews: 124,
  },
];

export default function ExpertInterview({ onFinish, onBack, onFeatureCreated, onQualitativeReady }: Props) {
  const [selectedExpert, setSelectedExpert] = useState<ExpertProfile | null>(null);
  const [isLive, setIsLive] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(true);

  // Real chat state
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState<{ role: "user" | "expert"; content: string; time: number }[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [sessionId] = useState(() => `interview_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const [isExtracting, setIsExtracting] = useState(false);
  const [suggestedFollowups, setSuggestedFollowups] = useState<string[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Real-time variable extraction state
  interface LiveExtract {
    id: number;
    text: string;
    type: "variable" | "belief" | "threshold" | "mechanism" | "boundary";
    value?: string;
    time: number;
  }
  const [liveExtracts, setLiveExtracts] = useState<LiveExtract[]>([]);
  const [sidebarTab, setSidebarTab] = useState<"transcript" | "variables" | "activities">("variables");
  const extractIdRef = useRef(0);

  // Timer
  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(interval);
  }, [isLive]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, isThinking]);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const availabilityColor = (a: string) => {
    if (a === "available") return "#16a34a";
    if (a === "busy") return "#d97706";
    return "#9ca3af";
  };

  // Extract variables/beliefs from a single expert response in real time
  const extractFromResponse = (text: string, time: number) => {
    const extracts: LiveExtract[] = [];
    const lines = text.split(/[.!?]+/).filter(s => s.trim().length > 15);
    const seenTexts = new Set<string>();

    for (const line of lines) {
      const lower = line.toLowerCase().trim();
      const snippet = line.trim().slice(0, 100);
      if (seenTexts.has(snippet)) continue;

      // Quantitative beliefs: percentages in any form
      const pctMatch = lower.match(
        /(\d+)[\s-]*(?:to|-)[\s-]*(\d+)\s*%|(\d+)\s*%|(\d+)\s*percent|(\d+)[\s-]*(?:to|-)[\s-]*(\d+)\s*percent/
      );
      // Numeric ranges with units (dollars, miles, minutes, etc)
      const rangeMatch = lower.match(
        /(\d+\.?\d*)\s*(?:to|-)\s*(\d+\.?\d*)\s*(?:standard deviations?|sd|points?|hours?|times?|weeks?|days?|months?|minutes?|miles?|dollars?|cents?|deliveries|stops?|per\s)/i
      );
      // Effect sizes, coefficients, correlations, any decimal preceded by qualifier
      const effectMatch = lower.match(
        /(?:effect size|coefficient|correlation|r\s*=|beta|impact|reduction|increase|improvement|drop|decline)\s*(?:of\s*|is\s*|around\s*|about\s*|roughly\s*|approximately\s*)?(\d*\.?\d+)/i
      );
      // Dollar amounts
      const dollarMatch = lower.match(
        /\$\s*(\d+\.?\d*)\s*(?:to|-)\s*\$?\s*(\d+\.?\d*)|\$\s*(\d+\.?\d*)/
      );
      // Standalone quantitative estimates: "I'd say 0.3", "roughly 0.25", "about 15", "around 40"
      const estimateMatch = !pctMatch && !rangeMatch && !effectMatch && !dollarMatch && lower.match(
        /(?:i'?d\s+(?:say|estimate|put|guess)|roughly|about|around|approximately|maybe|probably)\s+(\d+\.?\d*)/i
      );

      if (pctMatch) {
        extractIdRef.current += 1;
        extracts.push({ id: extractIdRef.current, type: "belief", text: snippet, time, value: pctMatch[0] });
        seenTexts.add(snippet);
      } else if (dollarMatch) {
        extractIdRef.current += 1;
        const val = dollarMatch[3] ? `$${dollarMatch[3]}` : `$${dollarMatch[1]}–$${dollarMatch[2]}`;
        extracts.push({ id: extractIdRef.current, type: "variable", text: snippet, time, value: val });
        seenTexts.add(snippet);
      } else if (rangeMatch) {
        extractIdRef.current += 1;
        extracts.push({ id: extractIdRef.current, type: "variable", text: snippet, time, value: `${rangeMatch[1]}–${rangeMatch[2]}` });
        seenTexts.add(snippet);
      } else if (effectMatch) {
        extractIdRef.current += 1;
        extracts.push({ id: extractIdRef.current, type: "variable", text: snippet, time, value: effectMatch[0] });
        seenTexts.add(snippet);
      } else if (estimateMatch) {
        extractIdRef.current += 1;
        extracts.push({ id: extractIdRef.current, type: "belief", text: snippet, time, value: estimateMatch[1] });
        seenTexts.add(snippet);
      }

      // Threshold/cutoff detection
      if (/threshold|cutoff|below\s+\d|above\s+\d|inflection|sweet spot|breaks?\s*down|tipping\s*point|diminishing\s*returns?|plateau|minimum|maximum|ceiling|floor|cap\b/.test(lower) && !seenTexts.has(snippet + "_threshold")) {
        extractIdRef.current += 1;
        extracts.push({
          id: extractIdRef.current, type: "threshold",
          text: snippet, time,
        });
      }

      // Mechanism detection
      if (/mechanism|pathway|operates? through|mediat|because|causes?|driven by|due to|results? from|leads? to|contributes? to|explains?/.test(lower) && !seenTexts.has(snippet + "_mechanism")) {
        extractIdRef.current += 1;
        extracts.push({ id: extractIdRef.current, type: "mechanism", text: snippet, time });
        seenTexts.add(snippet + "_mechanism");
      }

      // Boundary condition detection
      if (/breaks?\s*down|reverses?|doesn't hold|exception|fails?|doesn't work|opposite|only when|only if|not the case|won't apply|caveat|doesn't apply|edge case|outlier/.test(lower) && !seenTexts.has(snippet + "_boundary")) {
        extractIdRef.current += 1;
        extracts.push({ id: extractIdRef.current, type: "boundary", text: snippet, time });
        seenTexts.add(snippet + "_boundary");
      }
    }

    if (extracts.length > 0) {
      setLiveExtracts(prev => [...prev, ...extracts]);
      // Auto-switch to variables tab when first extract appears
      if (liveExtracts.length === 0) setSidebarTab("variables");
    }
  };

  const sendChatMessage = async (overrideMsg?: string) => {
    const msg = (overrideMsg || chatInput).trim();
    if (!msg || isThinking || !selectedExpert) return;
    setChatInput("");
    setSuggestedFollowups([]);
    setChatMessages(prev => [...prev, { role: "user", content: msg, time: elapsed }]);
    setIsThinking(true);

    try {
      const res = await interviewChat(sessionId, msg, {
        mode: "expert",
        expert_name: selectedExpert.name,
        expert_title: selectedExpert.title,
        expert_bio: selectedExpert.bio,
        variable_topic: "the predictive variable being discussed",
      });
      const reply = res.data.reply;
      setChatMessages(prev => [...prev, { role: "expert", content: reply, time: elapsed }]);

      // Show suggested follow-ups
      if (res.data.suggested_followups?.length) {
        setSuggestedFollowups(res.data.suggested_followups);
      }

      // Real-time extraction from the expert's response
      extractFromResponse(reply, elapsed);
    } catch (err) {
      console.error("Expert chat error:", err);
      setChatMessages(prev => [...prev, {
        role: "expert",
        content: "I'm having trouble connecting. Could you repeat that?",
        time: elapsed,
      }]);
    } finally {
      setIsThinking(false);
    }
  };

  const handleFinishInterview = async () => {
    if (!selectedExpert) return;
    setIsLive(false);
    setIsExtracting(true);

    try {
      // Get full transcript
      const transcriptRes = await getInterviewTranscript(sessionId);
      const transcript = transcriptRes.data.transcript;

      // Extract variables via legacy endpoint (for feature creation)
      try {
        const extractRes = await extractFromTranscript(transcript);
        const features = extractRes.data.features;
        if (features.length > 0 && onFeatureCreated) {
          onFeatureCreated(features[0]);
        }
      } catch (extractErr) {
        console.error("Variable extraction error:", extractErr);
      }

      // Claude-powered structured insight extraction
      let insights: ExtractedInsight[] = [];
      try {
        const insightsRes = await extractInsights(transcript, selectedExpert.name);
        const vars = insightsRes.data.variables || [];
        insights = vars.map((v: ExtractedInsightAPI, i: number) => ({
          id: i + 1,
          timestamp: formatTime(elapsed),
          text: v.description || v.source_quote || v.variable_name,
          type: (v.type === "variable" || v.type === "belief" || v.type === "threshold") ? v.type : "variable" as const,
          confidence: v.certainty,
          variable_name: v.variable_name,
          predictive_power: v.predictive_power,
          prior_mean: v.prior_mean,
          prior_std: v.prior_std,
          mechanism: v.mechanism,
          thresholds: v.thresholds,
          boundary_conditions: v.boundary_conditions,
          source_quote: v.source_quote,
        }));
      } catch (insightErr) {
        console.error("Claude insight extraction error:", insightErr);
        // Fallback: show raw expert responses without fake numbers
        insights = chatMessages
          .filter(m => m.role === "expert")
          .slice(0, 10)
          .map((m, i) => ({
            id: i + 1,
            timestamp: formatTime(m.time),
            text: m.content.slice(0, 120) + (m.content.length > 120 ? "..." : ""),
            type: "variable" as const,
            confidence: 0,
            variable_name: `Unextracted_Variable_${i + 1}`,
            mechanism: "Extraction failed — re-run with API connected",
          }));
      }

      // Run qualitative analysis in parallel (non-blocking)
      analyzeTranscript(transcript, sessionId)
        .then(qualRes => { if (onQualitativeReady) onQualitativeReady(qualRes.data); })
        .catch(qualErr => console.error("Qualitative analysis error:", qualErr));

      onFinish(insights, selectedExpert.name, elapsed, transcript);
    } catch (err) {
      console.error("Finish interview error:", err);
      onFinish([], selectedExpert.name, elapsed, "");
    } finally {
      setIsExtracting(false);
    }
  };

  // ── Expert Selection Grid ──────────────────────────────────
  if (!selectedExpert) {
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "20px 32px 16px", borderBottom: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {onBack && (
              <button onClick={onBack} className="btn btn-outline"
                style={{ width: 32, height: 32, padding: 0, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
            )}
            <div>
              <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0, marginBottom: 4, color: "#000" }}>
                Expert <span className="hl">interviews</span>
              </h2>
              <p style={{ margin: 0, fontSize: 12, color: "#000" }}>
                Select an expert to begin a structured elicitation session
              </p>
            </div>
          </div>
        </div>

        <div className="graph-paper" style={{ flex: 1, overflowY: "auto", padding: "24px 32px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, maxWidth: 1100 }}>
            {EXPERTS.map((expert, i) => (
              <div
                key={expert.id}
                onClick={() => expert.availability !== "offline" && setSelectedExpert(expert)}
                className="animate-in"
                style={{
                  background: "#fff", borderRadius: 20, padding: "24px 22px 20px",
                  boxShadow: "0 2px 16px rgba(0,0,0,0.04)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  cursor: expert.availability === "offline" ? "not-allowed" : "pointer",
                  opacity: expert.availability === "offline" ? 0.5 : 1,
                  transition: "all 0.2s ease",
                  animationDelay: `${i * 60}ms`,
                }}
                onMouseEnter={(e) => {
                  if (expert.availability !== "offline") {
                    e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,0.08)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "0 2px 16px rgba(0,0,0,0.04)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: 18,
                    background: expert.avatarBg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 18, fontWeight: 600, color: "rgba(0,0,0,0.6)",
                    border: "1px solid rgba(255,255,255,0.5)",
                    position: "relative",
                  }}>
                    {expert.initials}
                    <span style={{
                      position: "absolute", bottom: -2, right: -2,
                      width: 14, height: 14, borderRadius: "50%",
                      background: availabilityColor(expert.availability),
                      border: "2.5px solid #fff",
                    }} />
                  </div>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: "#000",
                    padding: "4px 10px", borderRadius: 120,
                    background: "rgba(0,0,0,0.04)",
                  }}>
                    ${expert.hourlyRate}/hr
                  </div>
                </div>

                <div style={{ fontSize: 15, fontWeight: 700, color: "#000", marginBottom: 2 }}>{expert.name}</div>
                <div style={{ fontSize: 11, color: "#000", marginBottom: 10, fontWeight: 500 }}>{expert.title}</div>
                <p style={{ fontSize: 11, color: "#000", margin: "0 0 12px", lineHeight: 1.6, fontWeight: 300 }}>{expert.bio}</p>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 14 }}>
                  {expert.credentials.map((cred) => (
                    <span key={cred} style={{
                      fontSize: 9, fontWeight: 500, padding: "3px 8px", borderRadius: 120,
                      background: "rgba(0,0,0,0.04)", color: "#000", whiteSpace: "nowrap",
                    }}>
                      {cred}
                    </span>
                  ))}
                </div>

                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  paddingTop: 12, borderTop: "1px solid rgba(0,0,0,0.04)", fontSize: 10, color: "#000",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="#000" opacity={0.7}>
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z" />
                      </svg>
                      {expert.rating}
                    </span>
                    <span>{expert.interviews} sessions</span>
                  </div>
                  <span className="hl" style={{ fontSize: 9, fontWeight: 600 }}>{expert.specialty}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Interview Session — real Claude-powered chat ──────────
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Interview header */}
      <div style={{ padding: "20px 32px 16px", borderBottom: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={() => { setSelectedExpert(null); setIsLive(false); setChatMessages([]); setElapsed(0); }}
              className="btn btn-outline"
              style={{ width: 32, height: 32, padding: 0, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div>
              <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 4px", color: "#000" }}>{selectedExpert.name}</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="tag" style={{ fontSize: 11 }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: isLive ? "#86efac" : "#000",
                    display: "inline-block",
                    animation: isLive ? "wf-pulse 2s ease infinite" : "none",
                  }} />
                  {isLive ? "Live" : "Ready"}
                </span>
                {isLive && (
                  <span style={{ fontSize: 12, color: "#000", fontFamily: "'SF Mono', monospace" }}>
                    {formatTime(elapsed)}
                  </span>
                )}
                <span style={{ fontSize: 11, color: "#000" }}>${selectedExpert.hourlyRate}/hr</span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {chatMessages.filter(m => m.role === "expert").length > 0 && (
              <span className="tag" style={{ fontSize: 11 }}>
                {chatMessages.filter(m => m.role === "expert").length} responses
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Left: Video + Chat area */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Video area */}
          <div style={{ padding: "16px 20px", flexShrink: 0 }}>
            <div style={{
              height: isLive ? 180 : 300, borderRadius: 20, overflow: "hidden", position: "relative",
              background: isLive
                ? "linear-gradient(135deg, #1a1a2e, #16213e, #0f3460)"
                : "linear-gradient(135deg, rgba(0,0,0,0.03), rgba(0,0,0,0.06))",
              border: isLive ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.06)",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.5s ease",
            }}>
              {!isLive ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <div style={{
                    width: 80, height: 80, borderRadius: 24, margin: "0 auto 24px",
                    background: selectedExpert.avatarBg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: "1px solid rgba(255,255,255,0.5)",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.06)",
                    fontSize: 28, fontWeight: 600, color: "rgba(0,0,0,0.5)",
                  }}>
                    {selectedExpert.initials}
                  </div>
                  <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px", color: "#000" }}>{selectedExpert.name}</h3>
                  <p style={{ fontSize: 13, color: "#000", margin: "0 0 4px" }}>{selectedExpert.title}</p>
                  <p style={{ fontSize: 12, color: "#000", margin: "0 0 28px", opacity: 0.5 }}>
                    Ask questions — {selectedExpert.name.split(" ")[0]} will respond with real domain expertise
                  </p>
                  <button
                    onClick={() => { setIsLive(true); setElapsed(0); setChatMessages([]); }}
                    className="btn btn-filled"
                    style={{ height: 48, padding: "0 36px", fontSize: 14, fontWeight: 500 }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" />
                    </svg>
                    Start Interview
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ textAlign: "center" }}>
                    <div style={{
                      width: 64, height: 64, borderRadius: "50%", margin: "0 auto 8px",
                      background: selectedExpert.avatarBg,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      border: "2px solid rgba(255,255,255,0.2)",
                      boxShadow: "0 0 40px rgba(196,181,253,0.3)",
                    }}>
                      <span style={{ fontSize: 22, fontWeight: 300, color: "rgba(255,255,255,0.9)" }}>{selectedExpert.initials}</span>
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 13, fontWeight: 500 }}>{selectedExpert.name}</div>
                    <div style={{ display: "flex", gap: 3, justifyContent: "center", marginTop: 8, height: 16, alignItems: "end" }}>
                      {[0.4, 0.7, 1, 0.6, 0.8, 1, 0.5, 0.9, 0.6, 0.3].map((h, i) => (
                        <div key={i} style={{
                          width: 3, borderRadius: 2, height: `${h * 16}px`,
                          background: isThinking ? "rgba(134,239,172,0.6)" : "rgba(255,255,255,0.2)",
                          animation: isThinking ? `wf-pulse ${1.5 + i * 0.2}s ease infinite` : "none",
                        }} />
                      ))}
                    </div>
                  </div>

                  {/* Self-view */}
                  <div style={{
                    position: "absolute", bottom: 12, right: 12,
                    width: 100, height: 70, borderRadius: 12,
                    background: "linear-gradient(135deg, #2a2a3e, #1a2a4e)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                  }}>
                    {isCameraOn ? (
                      <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>You</span>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5">
                        <line x1="1" y1="1" x2="23" y2="23" /><path d="M21 21H3a2 2 0 01-2-2V8a2 2 0 012-2h3l2-3h6" />
                      </svg>
                    )}
                  </div>

                  {/* REC badge */}
                  <div style={{
                    position: "absolute", top: 12, left: 12,
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "4px 10px", borderRadius: 120,
                    background: "rgba(0,0,0,0.5)", backdropFilter: "blur(8px)",
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ef4444", animation: "wf-pulse 1.5s ease infinite" }} />
                    <span style={{ fontSize: 10, color: "rgba(255,255,255,0.8)", fontFamily: "'SF Mono', monospace" }}>
                      REC {formatTime(elapsed)}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Controls bar */}
            {isLive && (
              <div style={{ display: "flex", justifyContent: "center", gap: 8, padding: "8px 0" }}>
                <button onClick={() => setIsMuted(!isMuted)} className="btn" style={{
                  width: 36, height: 36, padding: 0, borderRadius: "50%",
                  background: isMuted ? "rgba(239,68,68,0.15)" : "rgba(0,0,0,0.04)",
                  border: `1px solid ${isMuted ? "rgba(239,68,68,0.3)" : "rgba(0,0,0,0.06)"}`,
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" /><path d="M19 10v2a7 7 0 01-14 0v-2" />
                  </svg>
                </button>
                <button onClick={() => setIsCameraOn(!isCameraOn)} className="btn" style={{
                  width: 36, height: 36, padding: 0, borderRadius: "50%",
                  background: !isCameraOn ? "rgba(239,68,68,0.15)" : "rgba(0,0,0,0.04)",
                  border: `1px solid ${!isCameraOn ? "rgba(239,68,68,0.3)" : "rgba(0,0,0,0.06)"}`,
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Chat area — the real interview */}
          {isLive && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
              {/* Chat messages */}
              <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
                {chatMessages.length === 0 && !isThinking && (
                  <div style={{ textAlign: "center", padding: "20px 16px", color: "rgba(0,0,0,0.35)", fontSize: 12 }}>
                    Ask {selectedExpert.name.split(" ")[0]} about the variable you want to model. Be specific — ask about mechanisms, thresholds, effect sizes.
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {chatMessages.map((msg, i) => (
                    <div key={i} style={{
                      display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                    }}>
                      {msg.role === "expert" && (
                        <div style={{
                          width: 28, height: 28, borderRadius: 10, flexShrink: 0, marginRight: 8, marginTop: 2,
                          background: selectedExpert.avatarBg,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 10, fontWeight: 600, color: "rgba(0,0,0,0.5)",
                        }}>
                          {selectedExpert.initials}
                        </div>
                      )}
                      <div style={{
                        maxWidth: "80%", padding: "10px 14px", fontSize: 13, lineHeight: 1.65,
                        ...(msg.role === "user"
                          ? { background: "#000", color: "#fff", borderRadius: "16px 16px 4px 16px" }
                          : { background: "rgba(0,0,0,0.03)", color: "#000", border: "1px solid rgba(0,0,0,0.06)", borderRadius: "16px 16px 16px 4px" }),
                      }}>
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {isThinking && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: 10, flexShrink: 0,
                        background: selectedExpert.avatarBg,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10, fontWeight: 600, color: "rgba(0,0,0,0.5)",
                      }}>
                        {selectedExpert.initials}
                      </div>
                      <div style={{
                        padding: "10px 14px", borderRadius: "16px 16px 16px 4px",
                        background: "rgba(0,0,0,0.03)", border: "1px solid rgba(0,0,0,0.06)",
                        display: "flex", gap: 4, alignItems: "center",
                      }}>
                        <span style={{ animation: "typingDot 1.2s infinite 0s", fontSize: 12, color: "rgba(0,0,0,0.3)" }}>●</span>
                        <span style={{ animation: "typingDot 1.2s infinite 0.2s", fontSize: 12, color: "rgba(0,0,0,0.3)" }}>●</span>
                        <span style={{ animation: "typingDot 1.2s infinite 0.4s", fontSize: 12, color: "rgba(0,0,0,0.3)" }}>●</span>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              </div>

              {/* Suggested follow-ups */}
              {suggestedFollowups.length > 0 && !isThinking && (
                <div style={{
                  padding: "8px 16px 0", borderTop: "1px solid rgba(0,0,0,0.04)",
                  display: "flex", flexDirection: "column", gap: 6, flexShrink: 0,
                }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: "rgba(0,0,0,0.35)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Suggested follow-ups
                  </span>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {suggestedFollowups.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => sendChatMessage(q)}
                        style={{
                          padding: "6px 12px", fontSize: 12, fontFamily: "inherit",
                          background: "rgba(30,58,110,0.05)", color: "#1e3a6e",
                          border: "1px solid rgba(30,58,110,0.12)", borderRadius: 120,
                          cursor: "pointer", textAlign: "left", lineHeight: 1.4,
                          transition: "background 0.15s, border-color 0.15s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = "rgba(30,58,110,0.1)"; e.currentTarget.style.borderColor = "rgba(30,58,110,0.25)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "rgba(30,58,110,0.05)"; e.currentTarget.style.borderColor = "rgba(30,58,110,0.12)"; }}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Chat input */}
              <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendChatMessage()}
                    disabled={isThinking}
                    placeholder={`Ask ${selectedExpert.name.split(" ")[0]} a question...`}
                    style={{
                      flex: 1, padding: "10px 16px", background: "#fff",
                      border: "1px solid rgba(0,0,0,0.1)", borderRadius: 120,
                      fontSize: 13, fontFamily: "inherit", color: "#000", outline: "none",
                      opacity: isThinking ? 0.4 : 1,
                    }}
                  />
                  <button onClick={() => sendChatMessage()} disabled={isThinking} className="btn btn-filled" style={{ padding: "0 16px", borderRadius: 120 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          <style>{`
            @keyframes typingDot {
              0%, 60%, 100% { opacity: 0.2; transform: scale(0.8); }
              30% { opacity: 1; transform: scale(1); }
            }
          `}</style>
        </div>

        {/* Right sidebar — live transcript + variables */}
        <div style={{
          width: 320, flexShrink: 0, borderLeft: "1px solid rgba(0,0,0,0.06)",
          display: "flex", flexDirection: "column", background: "#fff",
        }}>
          {/* Sidebar tabs */}
          <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", gap: 4 }}>
            {(["variables", "activities", "transcript"] as const).map(tab => (
              <button key={tab} onClick={() => setSidebarTab(tab)} style={{
                flex: 1, padding: "6px 0", borderRadius: 6, border: "none",
                fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em",
                cursor: "pointer", fontFamily: "inherit",
                background: sidebarTab === tab ? "#000" : "rgba(0,0,0,0.04)",
                color: sidebarTab === tab ? "#fff" : "rgba(0,0,0,0.45)",
                transition: "all 0.15s ease",
                position: "relative",
              }}>
                {tab === "variables" ? "Variables" : tab === "activities" ? "Activities" : "Transcript"}
                {tab === "variables" && liveExtracts.length > 0 && (
                  <span style={{
                    position: "absolute", top: -4, right: -4,
                    width: 16, height: 16, borderRadius: "50%",
                    background: "#6366f1", color: "#fff",
                    fontSize: 9, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>{liveExtracts.length}</span>
                )}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
            {/* Variables tab — real-time extracted insights */}
            {sidebarTab === "variables" && (
              <>
                {liveExtracts.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px 12px" }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 16, margin: "0 auto 16px",
                      background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.1)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" style={{ opacity: 0.5 }}>
                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                    </div>
                    <p style={{ fontSize: 12, color: "rgba(0,0,0,0.4)", margin: 0, lineHeight: 1.5 }}>
                      Variables, beliefs, and thresholds will appear here as the expert mentions them
                    </p>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {/* Summary counts */}
                    <div style={{ display: "flex", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                      {(
                        [
                          ["variable", "#6366f1", "rgba(99,102,241,0.1)"],
                          ["belief", "#059669", "rgba(16,185,129,0.1)"],
                          ["threshold", "#d97706", "rgba(245,158,11,0.1)"],
                          ["mechanism", "#0ea5e9", "rgba(14,165,233,0.1)"],
                          ["boundary", "#dc2626", "rgba(239,68,68,0.1)"],
                        ] as const
                      ).map(([type, color, bg]) => {
                        const count = liveExtracts.filter(e => e.type === type).length;
                        if (count === 0) return null;
                        return (
                          <span key={type} style={{
                            fontSize: 9, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
                            background: bg, color,
                          }}>{count} {type}{count > 1 ? "s" : ""}</span>
                        );
                      })}
                    </div>

                    {/* Individual extracts */}
                    {liveExtracts.map((ext) => {
                      const typeConfig: Record<string, { color: string; bg: string; border: string }> = {
                        variable: { color: "#6366f1", bg: "rgba(99,102,241,0.04)", border: "rgba(99,102,241,0.15)" },
                        belief: { color: "#059669", bg: "rgba(16,185,129,0.04)", border: "rgba(16,185,129,0.15)" },
                        threshold: { color: "#d97706", bg: "rgba(245,158,11,0.04)", border: "rgba(245,158,11,0.15)" },
                        mechanism: { color: "#0ea5e9", bg: "rgba(14,165,233,0.04)", border: "rgba(14,165,233,0.15)" },
                        boundary: { color: "#dc2626", bg: "rgba(239,68,68,0.04)", border: "rgba(239,68,68,0.15)" },
                      };
                      const tc = typeConfig[ext.type] || typeConfig.variable;
                      return (
                        <div key={ext.id} style={{
                          padding: "8px 10px", borderRadius: 8,
                          background: tc.bg, borderLeft: `3px solid ${tc.border}`,
                          animation: "slideIn 0.3s ease",
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                            <span style={{
                              fontSize: 8, fontWeight: 700, textTransform: "uppercase",
                              letterSpacing: "0.06em", color: tc.color,
                            }}>{ext.type}</span>
                            <span style={{ fontSize: 8, color: "rgba(0,0,0,0.25)" }}>{formatTime(ext.time)}</span>
                            {ext.value && (
                              <span style={{
                                fontSize: 9, fontWeight: 700, color: tc.color,
                                fontFamily: "'IBM Plex Mono', monospace",
                                marginLeft: "auto",
                              }}>{ext.value}</span>
                            )}
                          </div>
                          <p style={{
                            margin: 0, fontSize: 10, color: "rgba(0,0,0,0.6)", lineHeight: 1.4,
                            fontStyle: "italic",
                          }}>"{ext.text}"</p>
                        </div>
                      );
                    })}
                  </div>
                )}
                <style>{`
                  @keyframes slideIn {
                    from { opacity: 0; transform: translateX(8px); }
                    to { opacity: 1; transform: translateX(0); }
                  }
                `}</style>
              </>
            )}

            {/* Activities tab */}
            {sidebarTab === "activities" && (
              <InterviewActivities onInsertPrompt={(prompt) => { setChatInput(prompt); setSidebarTab("variables"); }} />
            )}

            {/* Transcript tab */}
            {sidebarTab === "transcript" && (
              <>
                {chatMessages.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px 16px" }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 16, margin: "0 auto 16px",
                      background: "rgba(0,0,0,0.03)", border: "1px solid rgba(0,0,0,0.06)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="1.5" strokeLinecap="round" style={{ opacity: 0.4 }}>
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
                      </svg>
                    </div>
                    <p style={{ fontSize: 12, color: "#000", margin: 0, lineHeight: 1.5 }}>
                      {isLive ? "Transcript will appear here as you chat..." : "Start the interview to begin"}
                    </p>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {chatMessages.map((msg, i) => (
                      <div key={i} style={{
                        padding: "8px 10px", borderRadius: 8,
                        background: msg.role === "expert" ? "rgba(0,0,0,0.02)" : "transparent",
                        borderLeft: msg.role === "expert" ? "2px solid rgba(0,0,0,0.15)" : "2px solid rgba(0,0,0,0.06)",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                          <span style={{ fontSize: 10, fontWeight: 600, color: msg.role === "expert" ? "#000" : "rgba(0,0,0,0.5)" }}>
                            {msg.role === "expert" ? selectedExpert.name : "You"}
                          </span>
                          <span style={{ fontSize: 9, color: "rgba(0,0,0,0.25)" }}>{formatTime(msg.time)}</span>
                        </div>
                        <p style={{ margin: 0, fontSize: 11, color: "rgba(0,0,0,0.7)", lineHeight: 1.5 }}>
                          {msg.content}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {chatMessages.filter(m => m.role === "expert").length >= 2 && (
            <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
              <button
                onClick={handleFinishInterview}
                disabled={isExtracting}
                className="btn btn-filled"
                style={{ width: "100%", height: 44, fontSize: 13, fontWeight: 500, opacity: isExtracting ? 0.6 : 1 }}
              >
                {isExtracting ? (
                  <>
                    <div style={{
                      width: 14, height: 14, border: "2px solid rgba(255,255,255,0.3)",
                      borderTop: "2px solid #fff", borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }} />
                    Extracting variables...
                  </>
                ) : (
                  <>
                    Finish & Extract
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </>
                )}
              </button>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
